import { useCallback, useRef, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';

import { getVault, setVaultContact } from 'src/pages/admin/services/vaultService';

import { VAULTS_NS as NS } from './constants';
import {
  contactErrorMessages,
  GROUP_CHAT_ID_MAX,
  validateContactLink,
  X_HANDLE_MAX,
} from './contact';

/** The vault a contact edit is for. Its links are read fresh when the dialog opens, never taken from the row. */
export interface ContactTarget {
  vaultId: string;
  name: string;
}

export interface ContactErrors {
  xHandle?: string;
  groupChatId?: string;
}

/**
 * XBIT-4401: the contact dialog's state, shared by Vault Management and the applications list. Opening reads the vault
 * (GET /vault/vaults/:id) and prefills from the catalog row as it is now (ea's review I2): the row a list shows may be
 * stale, or on the applications list carry the request's links, and a full replace would put those back. A failed read
 * leaves nothing to save. One request at a time;
 * both links are sent every time (a full replace, blank as null). Whatever the answer, `onSettled` reads the list again,
 * so the row shows what the engine now holds. A replay (applied=false) is reported as such, not as a change.
 */
export const useVaultContact = ({ onSettled }: { onSettled: () => Promise<unknown> | void }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [target, setTarget] = useState<ContactTarget | null>(null);
  const [xHandle, setXHandle] = useState('');
  const [groupChatId, setGroupChatId] = useState('');
  const [errors, setErrors] = useState<ContactErrors>({});
  const [alert, setAlert] = useState<string[] | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [readFailed, setReadFailed] = useState(false);
  const inFlight = useRef(false);
  // The latest open wins: a slow read for a dialog since closed or reopened never fills the fields.
  const opened = useRef(0);

  const open = useCallback(
    async (next: ContactTarget) => {
      opened.current += 1;
      const seq = opened.current;
      setXHandle('');
      setGroupChatId('');
      setErrors({});
      setAlert(null);
      setReadFailed(false);
      setLoading(true);
      setTarget(next);
      try {
        const vault = await getVault(next.vaultId);
        if (seq !== opened.current) return;
        setXHandle(vault.xHandle ?? '');
        setGroupChatId(vault.groupChatId ?? '');
      } catch {
        if (seq !== opened.current) return;
        setReadFailed(true);
        setAlert([translate(`${NS}.contact.errors.readFailed`)]);
      } finally {
        if (seq === opened.current) setLoading(false);
      }
    },
    [translate]
  );

  const close = useCallback(() => {
    if (!inFlight.current) setTarget(null);
  }, []);

  const submit = useCallback(async () => {
    if (inFlight.current || !target || loading || readFailed) return;
    const invalid: ContactErrors = {
      xHandle: validateContactLink(xHandle, X_HANDLE_MAX),
      groupChatId: validateContactLink(groupChatId, GROUP_CHAT_ID_MAX),
    };
    setErrors(invalid);
    if (invalid.xHandle || invalid.groupChatId) return;
    inFlight.current = true;
    setSubmitting(true);
    try {
      const change = await setVaultContact(target.vaultId, {
        xHandle: xHandle.trim() || null,
        groupChatId: groupChatId.trim() || null,
      });
      setTarget(null);
      notify(`${NS}.contact.done.${change.applied ? 'applied' : 'replay'}`, {
        type: change.applied ? 'success' : 'info',
      });
    } catch (err) {
      setAlert(contactErrorMessages(err, translate));
    } finally {
      inFlight.current = false;
      setSubmitting(false);
      await onSettled();
    }
  }, [groupChatId, loading, notify, onSettled, readFailed, target, translate, xHandle]);

  return {
    target,
    xHandle,
    setXHandle,
    groupChatId,
    setGroupChatId,
    errors,
    alert,
    submitting,
    loading,
    readFailed,
    open,
    close,
    submit,
  };
};

export type VaultContactForm = ReturnType<typeof useVaultContact>;
